import React, { useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import MultiSelectPill, { MultiSelectPillSection, pillShape } from '../../Common/Filters/MultiSelectPill';
import { useSelectedBusiness } from '../useSelectedBusiness';
import { SharedSiteDto } from '../../../requests/useGetSharedSites';
import '../../Common/Filters/FilterPill.css';

const unassignedValue = '__unassigned';

export interface SiteFilter {
  values: string[];
  siteIds: string[];
  includeUnassigned: boolean;
  sharedSections: MultiSelectPillSection[];
  unassigned: boolean;
  setValues: (values: string[]) => void;
  matches: (sites: { id: string }[]) => boolean;
  emptyMessage: (fallback: string) => string;
}

export interface SiteFilterOptions {
  sharedSites?: SharedSiteDto[];
  unassigned?: boolean;
}

const sharedSiteSections = (sharedSites: SharedSiteDto[]): MultiSelectPillSection[] =>
  [...new Set(sharedSites.map(site => site.businessName))]
    .sort((first, second) => first.localeCompare(second))
    .map(businessName => ({
      label: businessName,
      note: 'shared',
      options: sharedSites
        .filter(site => site.businessName === businessName)
        .map(site => ({
          value: site.siteId,
          label: site.siteName,
          pillLabel: `${site.siteName} (${businessName})`,
        })),
    }));

export const useSiteFilter = ({ sharedSites = [], unassigned = true }: SiteFilterOptions = {}): SiteFilter => {
  const business = useSelectedBusiness();
  const [chosen, setValues] = useState<string[]>();

  const selectableSiteIds = [
    ...business.assignedSites.map(site => site.id),
    ...sharedSites.map(site => site.siteId),
  ];
  const offersUnassigned = unassigned && !business.siteRestricted;

  const values = chosen ?? [
    ...selectableSiteIds,
    ...(offersUnassigned ? [unassignedValue] : []),
  ];

  const includeUnassigned = values.includes(unassignedValue);
  const nothingSelected = values.length === 0;

  return {
    values,
    siteIds: values.filter(value => value !== unassignedValue),
    includeUnassigned,
    sharedSections: sharedSiteSections(sharedSites),
    unassigned: offersUnassigned,
    setValues,
    matches: sites => sites.length === 0
      ? includeUnassigned
      : sites.some(site => values.includes(site.id)),
    emptyMessage: fallback => nothingSelected && selectableSiteIds.length > 0
      ? 'No sites selected. Choose a site in the Sites filter to see this data.'
      : fallback,
  };
};

export const SiteFilterPill = ({ filter, debounceMs }: { filter: SiteFilter; debounceMs?: number }) => {
  const business = useSelectedBusiness();

  if (business.assignedSites.length === 0 && filter.sharedSections.length === 0) return null;

  return <MultiSelectPill
    id="sites"
    label="Sites"
    icon="geo-alt-fill"
    allLabel={business.siteRestricted ? undefined : 'All sites'}
    emptyLabel="No sites"
    values={filter.values}
    options={business.assignedSites.map(site => ({ value: site.id, label: site.name }))}
    sections={filter.sharedSections}
    extraOption={filter.unassigned
      ? { value: unassignedValue, label: 'Unassigned' }
      : undefined}
    debounceMs={debounceMs}
    onChange={filter.setValues} />;
};

export const StaticSiteFilterPill = ({ entities }: { entities: string }) => {
  const business = useSelectedBusiness();

  if (business.numberOfSites === 0) return null;

  const note = `${entities} are always shared across all sites.`;

  return <OverlayTrigger
    placement="bottom"
    overlay={
      <Popover className="border">
        <Popover.Body className="small py-2 px-3">{note}</Popover.Body>
      </Popover>}>
    <span
      className="filter-pill filter-pill-static d-inline-flex align-items-center text-nowrap border bg-white"
      style={pillShape}
      tabIndex={0}
      aria-label={`All sites. ${note}`}>
      <i className="bi-geo-alt-fill me-2" aria-hidden="true" />
      <span className="fw-semibold">All sites</span>
    </span>
  </OverlayTrigger>;
};
